import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Big from "big.js";
import { Link } from "react-router-dom";
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from "reactstrap";
/* Components */
import BlurredNumber from "../CommonForBoth/Blurred/BlurredNumber";
/* Selectors */
import { selectEthCallMultiple, selectLastTransact } from "@ensuro/ethereum-store";
import { getETokenList, getLoadingState } from "@store/eTokens/selectors";
import { selectUserAddress } from "@/store/user/selectors";
/* Store */
import { connect, useDispatch } from "react-redux";
/* Helpers */
import { addRemoveSub } from "@/helpers/store_helper";
import { makeWeTokenCalls } from "@/helpers/wetoken_helper";
/* i18n */
import { withTranslation } from "react-i18next";
import { currency } from "@/config";

const makeCallsForEtk = (etk, userAddress) => {
  const calls = [{ address: etk.address, abi: "EToken", method: "balanceOf", args: [userAddress] }];
  if (etk.custom_data?.wrapper?.address) {
    calls.push(...makeWeTokenCalls(etk.custom_data.wrapper, userAddress));
  }
  return calls;
};

export const makeEthCalls = (userAddress, eTokens) => {
  if (!userAddress) return [];
  return eTokens.flatMap((etk) => makeCallsForEtk(etk, userAddress));
};

export const MIN_VISIBLE_BALANCE = Big("0.01");

const isVisible = (value) => value != null && Big(value).gte(MIN_VISIBLE_BALANCE);

export const derivePositions = (eTokens, results) => {
  const rows = [];
  let index = 0;

  eTokens.forEach((etk) => {
    const plain = results[index++];
    const wrapper = etk.custom_data?.wrapper;
    let wrappedBalance;
    let wrappedRate;

    if (wrapper?.address) {
      wrappedBalance = results[index++];
      wrappedRate = results[index++];
    }

    if (plain?.state === "LOADED" && isVisible(plain.value)) {
      rows.push({
        key: `${etk.address}-plain`,
        address: etk.address,
        label: etk.name || etk.symbol,
        value: plain.value,
      });
    }

    if (wrapper?.address && wrappedBalance?.state === "LOADED" && wrappedRate?.state === "LOADED") {
      const value = Big(wrappedBalance.value).mul(Big(wrappedRate.value));
      if (isVisible(value)) {
        rows.push({
          key: `${etk.address}-wrapped`,
          address: etk.address,
          label: wrapper.symbol || wrapper.name,
          value,
        });
      }
    }
  });

  return rows;
};

export const TotalInvested = ({ t, userAddress, eTokens, eTokensLoading, ethResults, lastTx }) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);

  const calls = useMemo(() => makeEthCalls(userAddress, eTokens), [userAddress, eTokens]);

  useEffect(() => {
    if (!userAddress || !calls.length) return;
    return addRemoveSub(dispatch, "headerTotalInvested", [], calls);
  }, [dispatch, userAddress, calls]);

  useEffect(() => {
    if (!userAddress || !calls.length || lastTx?.state !== "MINED") return;
    calls.forEach((call) => dispatch({ type: "ETH_CALL", ...call, forceCall: true }));
  }, [dispatch, userAddress, calls, lastTx]);

  const loaded =
    Boolean(userAddress) &&
    eTokensLoading === "LOADED" &&
    ethResults.length === calls.length &&
    ethResults.every((result) => result?.state === "LOADED");

  const rows = loaded ? derivePositions(eTokens, ethResults) : [];
  const total = rows.reduce((acc, row) => acc.plus(row.value), Big(0));
  const hasPositions = loaded && total.gt(0);

  useEffect(() => {
    if (!hasPositions && open) setOpen(false);
  }, [hasPositions, open]);

  if (!userAddress) return null;

  const amount = loaded ? { value: total, state: "LOADED" } : { state: "LOADING" };
  const summary = (
    <div className="d-flex flex-column justify-content-center" style={{ lineHeight: 1.2 }}>
      <span className="font-size-12 text-white-50 header-total-label">{t("Total invested")}</span>
      <span className="font-size-16 fw-semibold" data-testid="total-invested-amount">
        <BlurredNumber number={amount} suffix={` ${currency.name}`} options={{ maximumFractionDigits: 2 }} />
      </span>
    </div>
  );

  if (!hasPositions) {
    return (
      <div className="header-item d-none d-lg-flex align-items-center px-3" data-testid="total-invested">
        {summary}
      </div>
    );
  }

  return (
    <Dropdown
      isOpen={open}
      toggle={() => setOpen((prev) => !prev)}
      className="d-none d-lg-flex"
      data-testid="total-invested"
    >
      <DropdownToggle
        tag="button"
        className="btn header-item d-flex align-items-center px-3"
        aria-label={t("Total invested")}
      >
        {summary}
        <div className="arrow-down ms-2" />
      </DropdownToggle>
      <DropdownMenu end>
        {rows.map((row) => (
          <DropdownItem key={row.key} tag={Link} to={`/eTokens/${row.address}`}>
            <div className="d-flex justify-content-between align-items-center" style={{ gap: 24 }}>
              <span>{row.label}</span>
              <span className="fw-semibold">
                <BlurredNumber
                  number={{ value: row.value, state: "LOADED" }}
                  suffix={` ${currency.name}`}
                  options={{ maximumFractionDigits: 2, notation: "standard" }}
                />
              </span>
            </div>
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
};

TotalInvested.propTypes = {
  t: PropTypes.any,
  userAddress: PropTypes.string,
  eTokens: PropTypes.array,
  eTokensLoading: PropTypes.string,
  ethResults: PropTypes.array,
  lastTx: PropTypes.object,
};

export const mapStateToProps = (state) => {
  const userAddress = selectUserAddress(state.UserReducer);
  const eTokens = getETokenList(state.ETokensReducer);
  const calls = makeEthCalls(userAddress, eTokens);
  const ethResults = userAddress ? selectEthCallMultiple(state.EthereumReducer, calls) : [];

  return {
    userAddress,
    eTokens,
    eTokensLoading: getLoadingState(state.ETokensReducer),
    ethResults,
    lastTx: selectLastTransact(state.EthereumReducer),
  };
};

export default connect(mapStateToProps)(withTranslation()(TotalInvested));
